import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Form, Input, Button, Table, Select, DatePicker, message } from 'antd';
import Gstyle from './index.module.less';
import layoutStyles from './sharePageLayout.module.less';
import useTableScrollY from './useTableScrollY';
import dayjs from 'dayjs';
import { knowledgeLogList, knowledgeLogOperationModules, knowledgeLogOperationTypes } from '@/api/knowledge';
import { API_ROOT } from '@/env';
import { downloadExportFile } from './downloadExport';
const { Content } = Layout;
const { RangePicker } = DatePicker;

function normalizeDictRows(res) {
    if (!res || res.data == null) {
        return [];
    }
    if (res.status !== 200 && res.success !== true) {
        return [];
    }
    const raw = res.data;
    if (Array.isArray(raw)) {
        return raw;
    }
    return raw.records ?? raw.list ?? raw.rows ?? [];
}

function buildListParams({ pageNo, pageSize, dateRange, creatorName, operationModule, operationType }) {
    const p = {
        pageNo,
        pageSize,
    };
    if (creatorName && String(creatorName).trim()) {
        p.creatorName = String(creatorName).trim();
    }
    if (dateRange && dateRange[0] && dateRange[1]) {
        p.createTimeStart = dateRange[0].startOf('day').format('YYYY-MM-DD');
        p.createTimeEnd = dateRange[1].endOf('day').format('YYYY-MM-DD');
    }
    if (operationModule) {
        p.operationModule = operationModule;
    }
    if (operationType) {
        p.operationType = operationType;
    }
    return p;
}

/** 导出：与列表相同的筛选条件（不带分页，由后端导出全部匹配数据） */
function buildExportParams(filters) {
    const { dateRange, creatorName, operationModule, operationType } = filters;
    const p = {};
    if (creatorName && String(creatorName).trim()) {
        p.creatorName = String(creatorName).trim();
    }
    if (dateRange && dateRange[0] && dateRange[1]) {
        p.createTimeStart = dateRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss');
        p.createTimeEnd = dateRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss');
    }
    if (operationModule) {
        p.operationModule = operationModule;
    }
    if (operationType) {
        p.operationType = operationType;
    }
    return p;
}

const Log = () => {
    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState([]);
    const [total, setTotal] = useState(0);
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [operationModuleOptions, setOperationModuleOptions] = useState([{ label: '全部', value: '' }]);
    const [operationTypeOptions, setOperationTypeOptions] = useState([{ label: '全部', value: '' }]);
    const [selectedOperationModule, setSelectedOperationModule] = useState('');
    const [loading, setLoading] = useState(false);
    const { tableWrapRef, tableScrollY } = useTableScrollY([
        dataSource.length,
        loading,
        total,
        pageSize,
    ]);

    /** 已生效的查询条件（点击「查询」后更新） */
    const [appliedFilters, setAppliedFilters] = useState({
        dateRange: null,
        creatorName: '',
        operationModule: '',
        operationType: '',
    });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await knowledgeLogOperationModules();
                if (cancelled) {
                    return;
                }
                const rows = normalizeDictRows(res);
                setOperationModuleOptions([
                    { label: '全部', value: '' },
                    ...rows.map(item => ({
                        label: item.dictLabel,
                        value: item.dictValue,
                    })),
                ]);
            } catch {
                if (!cancelled) {
                    setOperationModuleOptions([{ label: '全部', value: '' }]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!selectedOperationModule) {
            setOperationTypeOptions([{ label: '全部', value: '' }]);
            form.setFieldsValue({ operationType: '' });
            return () => {
                cancelled = true;
            };
        }
        (async () => {
            try {
                const res = await knowledgeLogOperationTypes({
                    moduleValue: selectedOperationModule,
                });
                if (cancelled) {
                    return;
                }
                const rows = normalizeDictRows(res);
                setOperationTypeOptions([
                    { label: '全部', value: '' },
                    ...rows.map(item => ({
                        label: item.dictLabel,
                        value: item.dictValue,
                    })),
                ]);
                form.setFieldsValue({ operationType: '' });
            } catch {
                if (!cancelled) {
                    setOperationTypeOptions([{ label: '全部', value: '' }]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedOperationModule, form]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildListParams({
                pageNo: current,
                pageSize,
                ...appliedFilters,
            });
            const res = await knowledgeLogList(params);
            if (res.status === 200 && res.data != null) {
                const data = res.data;
                const records = data.records ?? data.list ?? data.rows ?? [];
                setDataSource(Array.isArray(records) ? records : []);
                setTotal(data.total != null ? data.total : 0);
            } else {
                setDataSource([]);
                setTotal(0);
            }
        } catch (e) {
            message.error(e?.message || '获取列表失败');
            setDataSource([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [current, pageSize, appliedFilters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onSearch = () => {
        const values = form.getFieldsValue();
        setAppliedFilters({
            dateRange: values.dateRange,
            creatorName: values.creatorName,
            operationModule: values.operationModule,
            operationType: values.operationType,
        });
        setCurrent(1);
    };

    const onReset = () => {
        form.resetFields();
        setSelectedOperationModule('');
        setAppliedFilters({
            dateRange: null,
            creatorName: '',
            operationModule: '',
            operationType: '',
        });
        setCurrent(1);
    };

    const onExport = () => {
        downloadExportFile({
            url: `${API_ROOT}/knowledge/log/export`,
            method: 'post',
            params: buildExportParams(appliedFilters),
            fileName: `操作日志_${dayjs().format('YYYYMMDD')}.xlsx`,
        });
    };

    const columns = [
        { title: '日志ID', dataIndex: 'id', key: 'id', width: 200, ellipsis: true },
        { title: '操作人', dataIndex: 'creatorName', key: 'creatorName', width: 120, ellipsis: true },
        {
            title: '操作人ID',
            dataIndex: 'creatorId',
            key: 'creatorId',
            width: 200,
            ellipsis: true,
        },
        {
            title: '操作模块',
            dataIndex: 'operationModuleLabel',
            key: 'operationModuleLabel',
            width: 140,
            ellipsis: true,
        },
        {
            title: '操作类型',
            dataIndex: 'operationTypeLabel',
            key: 'operationTypeLabel',
            width: 140,
            ellipsis: true,
        },
        {
            title: '操作内容',
            dataIndex: 'operationDesc',
            width: 300,
            key: 'operationDesc',
        },
        {
            title: '操作时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 170,
            render: text => {
                if (!text) {
                    return ' ';
                }
                return dayjs(text).format('YYYY-MM-DD HH:mm:ss');
            },
        },
    ];

    return (
        <>
            <Layout className={layoutStyles.pageLayout}>
                <Content className={`${Gstyle.content} ${layoutStyles.pageContent}`}>
                    <Form
                        form={form}
                        layout="inline"
                        className={layoutStyles.inlineFilterForm}
                        style={{
                            marginBottom: 20,
                            flexShrink: 0,
                        }}
                        onValuesChange={changedValues => {
                            if ('operationModule' in changedValues) {
                                setSelectedOperationModule(changedValues.operationModule || '');
                            }
                        }}
                    >
                        <Form.Item label="操作时间" name="dateRange">
                            <RangePicker
                                placeholder={['开始日期', '结束日期']}
                                style={{ width: 260 }}
                            />
                        </Form.Item>
                        <Form.Item label="创建人姓名" name="creatorName">
                            <Input placeholder="请输入" style={{ width: 170 }} allowClear />
                        </Form.Item>
                        <Form.Item label="操作模块" name="operationModule" initialValue="">
                            <Select
                                placeholder="请选择"
                                options={operationModuleOptions}
                                allowClear
                                style={{ width: 170 }}
                            />
                        </Form.Item>
                        <Form.Item label="操作类型" name="operationType" initialValue="">
                            <Select
                                placeholder="请选择"
                                options={operationTypeOptions}
                                allowClear
                                disabled={!selectedOperationModule}
                                style={{ width: 170 }}
                            />
                        </Form.Item>
                        <div className={layoutStyles.searchFormActions}>
                            <Form.Item>
                                <Button type="primary" onClick={onSearch}>
                                    查询
                                </Button>
                                <Button type="primary" style={{ marginLeft: 30 }} onClick={onReset}>
                                    重置
                                </Button>
                                <Button type="primary" style={{ marginLeft: 30 }} onClick={onExport}>
                                    导出数据
                                </Button>
                            </Form.Item>
                        </div>
                    </Form>
                    <div ref={tableWrapRef} className={layoutStyles.tableWrap}>
                        <Table
                            scroll={tableScrollY != null ? { y: tableScrollY } : undefined}
                            className={Gstyle.table}
                            loading={loading}
                            columns={columns}
                            dataSource={dataSource}
                            rowKey={record => record.id ?? record.logId}
                            pagination={{
                                total: total,
                                pageSize: pageSize,
                                current: current,
                                showTotal: t => `共 ${t} 条`,
                                showQuickJumper: true,
                                showSizeChanger: true,
                                onChange: (page, ps) => {
                                    setCurrent(page);
                                    setPageSize(ps);
                                },
                            }}
                            rowClassName={(record, index) => {
                                const isMultipleOfTwo = (index + 1) % 2 === 0;
                                return isMultipleOfTwo ? Gstyle.tableRow : '';
                            }}
                        />
                    </div>
                </Content>
            </Layout>
        </>
    );
};

export default Log;
